import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { auth } from '@/lib/api';
import { ChevronLeft, ChevronRight, X, Sparkles, Check } from 'lucide-react';
import haptics from '@/lib/haptics';

/**
 * OnboardingTour
 * --------------
 * First-login guided walkthrough. Renders a transparent backdrop with a
 * spotlight cutout over the active step's anchor element + a tooltip card
 * with title/body + Skip / Back / Next / Finish controls.
 *
 * Anchors are looked up by `data-testid` (existing across the codebase).
 * Steps gracefully no-op if their anchor isn't on the current route — the
 * tour navigates to the appropriate route first.
 *
 * Persistence: localStorage(`apexflow.onboarding.v1`) per user-email key
 * stores `{ done: true, ts }`. Reset by clearing localStorage or via the
 * "Restart tour" button in /settings (future).
 */

const STORAGE_KEY = 'apexflow.onboarding.v1';

function loadState(email) {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const all = JSON.parse(raw);
    return all[email] || {};
  } catch { return {}; }
}

function saveState(email, state) {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const all = raw ? JSON.parse(raw) : {};
    all[email] = { ...(all[email] || {}), ...state };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
  } catch (_) { /* ignore quota errors */ }
}

const STEPS_FOR_ROLE = (role) => {
  const base = [
    { id: 'welcome', route: '/', anchor: null,
      title: 'Welcome to ApexFlow',
      body: 'Quick 60-second tour of the institutional control surface. You can skip anytime — restart it from Settings later.' },
    { id: 'dashboard', route: '/', anchor: 'dashboard-page',
      title: 'Trading Dashboard',
      body: 'Live order book, AI signals, multi-horizon ensemble, regime classifier, and the trade ticket all live here.' },
    { id: 'strategies', route: '/', anchor: 'nav-strategies',
      title: 'Strategy Catalog',
      body: 'Build, backtest, and deploy strategies. Each strategy has a rank, capacity, and live PnL — top performers get more capital allocated.' },
    { id: 'portfolio', route: '/', anchor: 'nav-portfolio',
      title: 'Portfolio Lab',
      body: 'Multi-account portfolio view with regime-Kelly sizing, real-PnL bandit, and per-fund risk envelopes.' },
    { id: 'venues', route: '/', anchor: 'nav-venues',
      title: 'Venues & Live Trading',
      body: 'Connect Binance / Coinbase / Kraken keys here. Once enabled, the smart order router fan-outs across venues for best execution.' },
    { id: 'banking', route: '/', anchor: 'nav-banking',
      title: 'Banking · Stripe Rails',
      body: 'Real USD deposits via Stripe Checkout and withdrawals via Stripe Connect Express. KYC gate runs before any money movement.' },
  ];
  if (role === 'admin') {
    base.push({
      id: 'admin', route: '/', anchor: 'nav-admin',
      title: 'Admin · 5-Tab Control Pack',
      body: 'EXECUTION · RISK · OPS · COMPLIANCE · USERS. Cutover staging, kill-switch, audit PDF, latency SLOs, and the Stripe webhook events viewer all live in here.',
    });
  }
  base.push({
    id: 'done', route: null, anchor: null,
    title: 'You are ready',
    body: 'Press ⌘K (or Ctrl-K) anywhere for a fast command palette. Happy trading.',
  });
  return base;
};

export default function OnboardingTour() {
  const navigate = useNavigate();
  const location = useLocation();
  const user = auth.currentUser();
  const [active, setActive] = useState(false);
  const [idx, setIdx] = useState(0);
  const [rect, setRect] = useState(null);
  const [cardDragX, setCardDragX] = useState(0);
  const observerRef = useRef(null);
  const cardDragStart = useRef(null);

  const email = user?.email || '_anon';
  const role = user?.role || 'operator';
  const steps = STEPS_FOR_ROLE(role);
  const step = steps[idx];

  // Boot: only run on /, only if not done, only if logged in.
  useEffect(() => {
    if (!user) return;
    const s = loadState(email);
    if (s.done) return;
    if (location.pathname !== '/') return;
    // Slight delay so dashboard panels mount first.
    const t = setTimeout(() => setActive(true), 800);
    return () => clearTimeout(t);
  }, [email, user, location.pathname]);

  // Compute spotlight rect for the current step's anchor.
  const measure = useCallback(() => {
    if (!step?.anchor) { setRect(null); return; }
    const el = document.querySelector(`[data-testid="${step.anchor}"]`);
    if (!el) { setRect(null); return; }
    const r = el.getBoundingClientRect();
    setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
  }, [step]);

  useEffect(() => {
    if (!active) return;
    measure();
    const onScrollResize = () => measure();
    window.addEventListener('scroll', onScrollResize, true);
    window.addEventListener('resize', onScrollResize);
    return () => {
      window.removeEventListener('scroll', onScrollResize, true);
      window.removeEventListener('resize', onScrollResize);
    };
  }, [active, measure]);

  if (!active || !step) return null;

  const finish = (markDone = true) => {
    if (markDone) {
      saveState(email, { done: true, ts: Date.now() });
      haptics.confirm();
    } else {
      haptics.dismiss();
    }
    setActive(false);
  };
  const next = () => {
    if (idx >= steps.length - 1) { finish(true); return; }
    haptics.tick();
    setIdx(idx + 1);
  };
  const back = () => {
    if (idx > 0) { haptics.tick(); setIdx(idx - 1); }
  };

  // Swipe-to-dismiss state — drag the card horizontally past 80 px to
  // skip the tour. Visual feedback: card translates with the finger and
  // fades as it moves further.
  const onCardTouchStart = (e) => {
    const t = e.touches[0];
    cardDragStart.current = { x: t.clientX, y: t.clientY, axis: null };
  };
  const onCardTouchMove = (e) => {
    if (!cardDragStart.current) return;
    const t = e.touches[0];
    const dx = t.clientX - cardDragStart.current.x;
    const dy = t.clientY - cardDragStart.current.y;
    if (!cardDragStart.current.axis) {
      if (Math.abs(dx) > 8 || Math.abs(dy) > 8) {
        cardDragStart.current.axis =
          Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
      }
    }
    if (cardDragStart.current.axis === 'x') {
      e.preventDefault();
      setCardDragX(dx);
    }
  };
  const onCardTouchEnd = () => {
    if (Math.abs(cardDragX) > 80) {
      // dismiss with a soft buzz, then close
      finish(true);
    }
    setCardDragX(0);
    cardDragStart.current = null;
  };

  // Tooltip placement — below spotlight if there's room, else above.
  // Clamp width on narrow viewports so it never escapes the screen.
  const vpw = typeof window !== 'undefined' ? window.innerWidth : 1024;
  const isMobile = vpw < 640;
  const cardWidth = isMobile ? vpw - 24 : Math.min(360, vpw - 24);
  let tipStyle = {
    position: 'fixed', zIndex: 10001,
    width: cardWidth, maxWidth: 'calc(100vw - 24px)',
    padding: 16,
    background: '#0a0a0a',
    border: '1px solid var(--accent-amber)',
    boxShadow: '0 8px 32px rgba(0,0,0,0.6)',
    borderRadius: 6,
  };
  if (isMobile) {
    // On phones, dock the tooltip to the bottom-center as a sheet so it
    // never clips off-screen regardless of anchor position.
    tipStyle.left = 12;
    tipStyle.right = 12;
    tipStyle.bottom = 84;
    tipStyle.width = 'auto';
  } else if (rect) {
    const below = rect.top + rect.height + 24;
    const above = rect.top - 24;
    const wantBelow = below + 200 < window.innerHeight;
    if (wantBelow) {
      tipStyle.top = below;
    } else {
      tipStyle.top = Math.max(20, above - 220);
    }
    tipStyle.left = Math.max(
      12,
      Math.min(rect.left, vpw - cardWidth - 12),
    );
  } else {
    // Center if no anchor (desktop fallback).
    tipStyle.top = '50%';
    tipStyle.left = '50%';
    tipStyle.transform = 'translate(-50%, -50%)';
  }

  return (
    <div data-testid="onboarding-tour-root">
      {/* Backdrop with cutout */}
      <div
        data-testid="onboarding-tour-backdrop"
        style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0, 0, 0, 0.65)',
          zIndex: 10000,
          pointerEvents: 'auto',
        }}
        onClick={(e) => { if (e.target === e.currentTarget) {/* keep */} }}
      />
      {/* Spotlight ring */}
      {rect && (
        <div
          data-testid="onboarding-tour-spotlight"
          style={{
            position: 'fixed',
            top: rect.top - 6,
            left: rect.left - 6,
            width: rect.width + 12,
            height: rect.height + 12,
            border: '2px solid var(--accent-amber)',
            boxShadow: '0 0 0 9999px rgba(0,0,0,0.65)',
            borderRadius: 4,
            zIndex: 10000,
            pointerEvents: 'none',
            transition: 'all 200ms ease',
          }}
        />
      )}
      {/* Tooltip card */}
      <div data-testid="onboarding-tour-card"
           onTouchStart={onCardTouchStart}
           onTouchMove={onCardTouchMove}
           onTouchEnd={onCardTouchEnd}
           style={{
             ...tipStyle,
             transform: cardDragX !== 0
               ? `translateX(${cardDragX}px)`
               : tipStyle.transform,
             opacity: 1 - Math.min(0.6, Math.abs(cardDragX) / 200),
             transition: cardDragX === 0
               ? 'transform 220ms ease, opacity 220ms ease'
               : 'none',
           }}>
        <div className="flex items-center justify-between"
             style={{ marginBottom: 8 }}>
          <div className="flex items-center gap-2"
               style={{ color: 'var(--accent-amber)',
                        fontSize: 11, fontWeight: 700,
                        letterSpacing: '0.1em' }}>
            <Sparkles size={11} />
            STEP {idx + 1} / {steps.length}
          </div>
          <button onClick={() => finish(true)}
                  data-testid="onboarding-tour-skip"
                  className="btn-sharp"
                  style={{ padding: '2px 8px', fontSize: 10 }}>
            <X size={10} style={{ display: 'inline', marginRight: 3 }} />
            SKIP
          </button>
        </div>
        <div data-testid="onboarding-tour-title"
             style={{ fontSize: 18, fontWeight: 800,
                      color: 'var(--text-primary)', marginBottom: 6 }}>
          {step.title}
        </div>
        <div data-testid="onboarding-tour-body"
             style={{ fontSize: 13, lineHeight: 1.55,
                      color: 'var(--text-secondary)', marginBottom: 14 }}>
          {step.body}
        </div>
        <div className="flex items-center justify-between">
          <div style={{ display: 'flex', gap: 4 }}>
            {steps.map((_, i) => (
              <span key={i}
                    style={{ width: 6, height: 6, borderRadius: '50%',
                             background: i === idx
                               ? 'var(--accent-amber)'
                               : i < idx
                                 ? 'var(--accent-green, #22c55e)'
                                 : '#333' }} />
            ))}
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            {idx > 0 && (
              <button onClick={back}
                      data-testid="onboarding-tour-back"
                      className="btn-sharp"
                      style={{ padding: '4px 10px', fontSize: 11,
                               display: 'inline-flex', alignItems: 'center',
                               gap: 3 }}>
                <ChevronLeft size={11} /> BACK
              </button>
            )}
            <button onClick={next}
                    data-testid={
                      idx >= steps.length - 1
                        ? 'onboarding-tour-finish'
                        : 'onboarding-tour-next'
                    }
                    className="btn-sharp"
                    style={{ padding: '4px 12px', fontSize: 11,
                             background: 'var(--accent-amber)',
                             color: '#0a0a0a', fontWeight: 700,
                             border: 'none',
                             display: 'inline-flex', alignItems: 'center',
                             gap: 3 }}>
              {idx >= steps.length - 1
                ? <><Check size={11} /> FINISH</>
                : <>NEXT <ChevronRight size={11} /></>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
